import { memoize } from './memoize';

/*
    Tap requirements:
    1) Only one point of contact
    2) Time between TouchStart and TouchEnd must be less than the tap timeout (200ms)
    3) The touch can only move a certain threshold (10 px) before being canceled
    4) TouchCancel events cancel the tap
*/

const TAP_TIMEOUT = 200;
const MOVE_THRESHOLD = 100;
const REJECT_CLICK = true;
export default class TapListener {
    constructor(props = {}) {
        const {
            tapTimeout = TAP_TIMEOUT,
            moveThreshold = MOVE_THRESHOLD,
            rejectClick = REJECT_CLICK,
        } = props;

        this._tapTimeout = tapTimeout;
        this._moveThreshold = moveThreshold;
        this._rejectClick = rejectClick;

        this.listener = undefined;
        this._el = undefined;
        this._useCapture = false;
        this._timeout = undefined;

        this._startCoords = {};
        this._canceled = false;
        this._memoizedCalcDistance = memoize(this._calcDistance);
    }

    addListener(el, listener, useCapture) {
        this.listener = listener;
        this._el = el;
        this._useCapture = useCapture;

        try {
            this._assertHasEl();
            this._assertHasListener();
            this._addTouchStartListener();
        } catch (ex) {
            throw ex;
        }
    }

    removeListener() {
        this._el.removeEventListener('touchstart', this._onTouchStart, this._useCapture);
        this._el.removeEventListener('touchmove', this._onTouchMove, this._useCapture);
        this._el.removeEventListener('touchcancel', this._onTapCancel, this._useCapture);
        this._el.removeEventListener('touchend', this._onTouchEnd, this._useCapture);
    }

    _addTouchStartListener() {
        this._el.addEventListener('touchstart', this._onTouchStart, this._useCapture);
    }

    _onTouchStart = (e) => {
        try {
            this._assertOnlyOneTouchPoint(e);
            this._setStartCoords(e);
            this._addOtherTouchListeners();
            this._startTimer();
        } catch (ex) {

            // move along
        }
    }

    _setStartCoords(e) {
        const touch = e.touches[0];
        this._startCoords = { x: touch.clientX, y: touch.clientY };
    }

    _addOtherTouchListeners() {
        this._el.addEventListener('touchmove', this._onTouchMove, this._useCapture);
        this._el.addEventListener('touchcancel', this._onTapCancel, this._useCapture);
        this._el.addEventListener('touchend', this._onTouchEnd, this._useCapture);
    }

    _startTimer() {
        this._timeout = setTimeout(() => this._cancelTap(), this._tapTimeout);
    }

    _onTouchMove = (e) => {
        try {
            this._assertOnlyOneTouchPoint(e);
            this._assertMoveDistanceInsideThreshold(e);
        } catch (ex) {
            this._cancelTap();
            this._cancelTimer();
        }
    }

    _cancelTap() {
        this._canceled = true;
    }

    _cancelTimer() {
        clearTimeout(this._timeout);
    }

    _onTapCancel = (e) => {
        this._resetTap();
    }

    _resetTap() {
        this._canceled = false;
        this._cancelTimer();
        this._removeAllButStartListeners();
    }

    _removeAllButStartListeners() {
        this._el.addEventListener('touchmove', this._onTouchMove, this._useCapture);
        this._el.addEventListener('touchcancel', this._onTapCancel, this._useCapture);
        this._el.addEventListener('touchend', this._onTouchEnd, this._useCapture);
    }

    _onTouchEnd = (e) => {
        try {
            this._assertNotCanceled();
            this._assertOnlyOneTouchPoint(e);

            this.listener(e);
            if (this._rejectClick) {
                e.preventDefault();
            }
        } catch (ex) {
            // move along
        }

        this._resetTap();
    }

    _assertHasEl() {
        if (!this._el) {
            throw new Error('The element to listen to tap events on must be specified');
        }
    }

    _assertHasListener() {
        if (!this.listener || typeof this.listener !== 'function') {
            throw new Error('The listener function must be specified');
        }
    }

    _assertOnlyOneTouchPoint(e) {
        if (e.touches.length > 1) {
            throw new Error('More than one touch point');
        }
    }

    _assertNotCanceled() {
        if (this._canceled) {
            throw new Error('Tap was canceled');
        }
    }

    _assertMoveDistanceInsideThreshold(e) {
        const touch = e.touches[0];
        const currentCoord = { x: touch.clientX, y: touch.clientY };
        const distance = this._memoizedCalcDistance(this._startCoords, currentCoord);
        if (distance > this._moveThreshold) {
            throw new Error(`Touch has moved more than ${this._moveThreshold} px`);
        }
    }

    _calcDistance(startCoords, endCoords) {
        const deltaX = endCoords.x - startCoords.x;
        const deltaY = endCoords.y - startCoords.y;
        const distance = (deltaX ** 2 + deltaY ** 2) ** 0.5;
        return Math.abs(distance);
    }
}
